import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from '../../../redux/reducers';
import * as StorageBrowserStore from '../../../redux/middleware/StorageBrowser.Middleware';
import * as AzureFileUploadStore from '../../../redux/middleware/AzureFileUpload.Middleware';
import { AzureFileListLoaderKind, AzureFileTarget, UploadSasToken } from '../../../models/Entities/AzureFiles/AzureFileUpload';
import {
  StorageBreadcrumbViewModel,
  StorageCurrentFolderViewModel,
  StorageFileAction,
  StorageFolderRenameViewModel,
  StorageFileViewModel,
  StorageFolderViewModel,
} from '../../../models/Entities/AzureFiles/StorageBrowser';
import { ErrorType } from '../../../models/HttpError';
import {
  StorageEntityRef,
  StorageFileKind,
  StorageOwnerNames,
  Translate,
  buildStorageOwnerNames,
  getFriendlyFolderName,
  getStorageFileExtension,
  getStorageFileKind,
  matchesStorageSearch,
} from '../../../functions/StorageBrowser.Functions';
import { isSystemFolder } from '../../../functions/StorageManagement.Functions';

//#region Props

interface IProps {
  /** Folder to show, relative to the tenant root ("" = root). Comes from ?path= */
  path: string;
  /** Navigates to another folder (the page pushes it to the URL so "back" works) */
  onNavigate: (path: string, options?: { replace?: boolean }) => void;
}

interface ConnectedProps {
  currentPath: string;
  breadcrumbs: StorageBreadcrumbViewModel[];
  folders: StorageFolderViewModel[];
  files: StorageFileViewModel[];
  continuationToken: string | null;
  /** Capabilities of the folder (canUpload / canCreateFolder / allowedExtensions) */
  currentFolder: StorageCurrentFolderViewModel | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  isLoaded: boolean;
  error: ErrorType | undefined;
  pendingFile: { path: string; action: StorageFileAction } | undefined;
  fileError: { path: string; action: StorageFileAction; error: ErrorType } | undefined;
  ownerNames: StorageOwnerNames;
}

interface DispatchProps {
  BrowseStorage: (path: string, continuationToken?: string | null) => Promise<void>;
  RequestReadSas: (path: string, action: StorageFileAction) => Promise<UploadSasToken>;
  ClearFileError: () => void;
  ResolveEntityNames: (refs: StorageEntityRef[]) => Promise<void>;
  CreateFolder: (parentPath: string, name: string) => Promise<StorageFolderViewModel>;
  RenameFolder: (path: string, newName: string) => Promise<StorageFolderRenameViewModel>;
  DeleteFolder: (path: string) => Promise<void>;
  DeleteFile: (path: string) => Promise<void>;
  UploadToFolder: (folderPath: string, file: File, onProgress?: (percent: number) => void) => Promise<string>;
  /** Registers a blob in its owner service (auto-register after uploading into an entity folder, spec 005) */
  RegisterAzureFile: (kind: AzureFileListLoaderKind, target: AzureFileTarget, path: string) => Promise<unknown>;
}

export type Props = IProps & ConnectedProps & DispatchProps;

// Entity names are derived from other slices: keep the same object while those sources don't change.
let ownerNamesCache: { sources: unknown[]; names: StorageOwnerNames } | undefined;

/**
 * Names of providers, sponsors, protocols and settlements known by the app (store lists plus
 * names resolved by id), keyed by getEntityNameKey. Shared by the explorer, the upload drawer
 * and the file preview. Memoized on its sources.
 */
export const selectStorageOwnerNames = (state: AppState): StorageOwnerNames => {
  const sources = [
    state.Provider?.list,
    state.Sponsor?.list,
    state.MedicalProtocols?.list,
    state.Settlement?.list,
    state.StorageBrowser?.entityNames,
    state.Practitioners?.list,
    state.Services?.list,
    state.Contract?.list,
  ];
  if (!ownerNamesCache || ownerNamesCache.sources.some((source, index) => source !== sources[index])) {
    ownerNamesCache = {
      sources,
      names: buildStorageOwnerNames(
        state.Provider?.list,
        state.Sponsor?.list,
        state.MedicalProtocols?.list,
        state.Settlement?.list,
        state.StorageBrowser?.entityNames,
        state.Practitioners?.list,
        state.Services?.list,
        state.Contract?.list
      ),
    };
  }
  return ownerNamesCache.names;
};

export const mapStateToProps = (state: AppState): ConnectedProps => {
  const browser = state.StorageBrowser;
  return {
    currentPath: browser?.currentPath ?? '',
    breadcrumbs: browser?.breadcrumbs ?? [],
    folders: browser?.folders ?? [],
    files: browser?.files ?? [],
    continuationToken: browser?.continuationToken ?? null,
    currentFolder: browser?.currentFolder ?? null,
    isLoading: browser?.isLoading ?? false,
    isLoadingMore: browser?.isLoadingMore ?? false,
    isLoaded: browser?.isLoaded ?? false,
    error: browser?.error,
    pendingFile: browser?.pendingFile,
    fileError: browser?.fileError,
    ownerNames: selectStorageOwnerNames(state),
  };
};

export const mapDispatchToProps = (dispatch: ThunkDispatch<AppState, unknown, AnyAction>): DispatchProps => ({
  BrowseStorage: (path, continuationToken) =>
    dispatch(StorageBrowserStore.ActionCreators.BrowseStorage(path, continuationToken)),
  RequestReadSas: (path, action) => dispatch(StorageBrowserStore.ActionCreators.RequestReadSas(path, action)),
  ClearFileError: () => {
    dispatch(StorageBrowserStore.ActionCreators.ClearFileError());
  },
  ResolveEntityNames: (refs) => dispatch(StorageBrowserStore.ActionCreators.ResolveEntityNames(refs)),
  CreateFolder: (parentPath, name) => dispatch(StorageBrowserStore.ActionCreators.CreateFolder(parentPath, name)),
  RenameFolder: (path, newName) => dispatch(StorageBrowserStore.ActionCreators.RenameFolder(path, newName)),
  DeleteFolder: (path) => dispatch(StorageBrowserStore.ActionCreators.DeleteFolder(path)),
  DeleteFile: (path) => dispatch(StorageBrowserStore.ActionCreators.DeleteFile(path)),
  UploadToFolder: (folderPath, file, onProgress) =>
    dispatch(StorageBrowserStore.ActionCreators.UploadToFolder(folderPath, file, onProgress)),
  RegisterAzureFile: (kind, target, path) =>
    dispatch(AzureFileUploadStore.ActionCreators.RegisterAzureFile(kind, target, path)),
});

//#endregion

//#region Explorer items

export type ExplorerViewMode = 'list' | 'grid';

export interface ExplorerItem {
  key: string;
  kind: 'folder' | 'file';
  /** Real name in the storage (shown in the tooltip) */
  name: string;
  /** Friendly name for folders, real name for files */
  displayName: string;
  path: string;
  /** folder, pdf, spreadsheet, ... */
  iconKind: StorageFileKind | 'folder';
  extension: string;
  size?: number;
  lastModified?: string;
  /** Folders: managed by the system (cannot be renamed nor deleted) */
  isSystem?: boolean;
}

/** Folders first (server order: by name), then files (server order: lastModified desc). */
export const buildExplorerItems = (
  folders: StorageFolderViewModel[],
  files: StorageFileViewModel[],
  t: Translate,
  ownerNames: StorageOwnerNames
): ExplorerItem[] => [
  ...folders.map(
    (folder): ExplorerItem => ({
      key: `folder:${folder.path}`,
      kind: 'folder',
      name: folder.name,
      displayName: getFriendlyFolderName(folder.name, t, ownerNames),
      path: folder.path,
      iconKind: 'folder',
      extension: '',
      isSystem: isSystemFolder(folder),
    })
  ),
  ...files.map((file): ExplorerItem => {
    const extension = getStorageFileExtension(file);
    return {
      key: `file:${file.path}`,
      kind: 'file',
      name: file.name,
      displayName: file.name,
      path: file.path,
      iconKind: getStorageFileKind(extension),
      extension,
      size: file.size,
      lastModified: file.lastModified,
    };
  }),
];

/** Search inside the current folder (real and friendly names, case/accent insensitive). */
export const filterExplorerItems = (items: ExplorerItem[], search: string): ExplorerItem[] =>
  search.trim() ? items.filter((item) => matchesStorageSearch([item.name, item.displayName], search)) : items;

/** Breadcrumb trail: root "Mis archivos" + server breadcrumbs (friendly names). */
export interface ExplorerBreadcrumb {
  key: string;
  path: string;
  name: string;
  displayName: string;
}

export const buildExplorerBreadcrumbs = (
  breadcrumbs: StorageBreadcrumbViewModel[],
  currentPath: string,
  rootLabel: string,
  t: Translate,
  ownerNames: StorageOwnerNames
): ExplorerBreadcrumb[] => {
  // Until the server answers, derive the trail from the path itself.
  const source: StorageBreadcrumbViewModel[] =
    breadcrumbs.length > 0 || !currentPath
      ? breadcrumbs
      : currentPath.split('/').map((name, index, all) => ({ name, path: all.slice(0, index + 1).join('/') }));
  return [
    { key: 'root', path: '', name: rootLabel, displayName: rootLabel },
    ...source.map((crumb) => ({
      key: crumb.path,
      path: crumb.path,
      name: crumb.name,
      displayName: getFriendlyFolderName(crumb.name, t, ownerNames),
    })),
  ];
};

//#endregion

//#region Browser side effects (open / download with the SAS)

/**
 * Opens an empty tab synchronously (inside the click), so the popup blocker allows it;
 * its location is set once the SAS arrives. Returns null when the browser blocked it.
 */
export const openPendingTab = (): Window | null => {
  try {
    return window.open('', '_blank');
  } catch {
    return null;
  }
};

/** Points the pending tab to the SAS url (or opens a new one when it was blocked). */
export const showInTab = (tab: Window | null, url: string): void => {
  if (tab && !tab.closed) {
    tab.opener = null;
    tab.location.href = url;
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const closePendingTab = (tab: Window | null): void => {
  if (tab && !tab.closed) tab.close();
};

/** Downloads through a temporary anchor (the SAS carries Content-Disposition: attachment). */
export const triggerDownload = (url: string, fileName: string): void => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener noreferrer';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

//#endregion
